import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  VStack,
  Box,
  Flex,
  TextInput,
  ModalCloseButton,
  Modal,
  ModalBody,
  ModalHeader,
  Alert,
  ModalFooterButtons,
  useToast,
} from '@televet/kibble-ui';
import { IAttachmentOption } from '../../../../../interfaces/IAttachmentOption';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import PromptPreview from './FillInTheBlankPromptPreview';
import { IChannelDraft } from 'pages/Conversations/interfaces/IChannelDraft';
import { IQuestion } from 'pages/Forms/FormsContent/FormsDrafting/Question';

interface IFillInTheBlank {
  title: string;
  body: string;
}

interface IOpenFormFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachment: IAttachmentOption | undefined;
  channelDraft: IChannelDraft;
  setChannelDraft: (channelDraft: Partial<IChannelDraft>) => void;
}

const OpenFormFieldModal = ({
  isOpen,
  onClose,
  attachment,
  channelDraft,
  setChannelDraft,
}: IOpenFormFieldModalProps): JSX.Element => {
  const toast = useToast();
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);

  const channelDraftInputValues = useMemo(() => {
    if (!attachment?.id) return [];

    const draftValues = channelDraft.fillInTheBlankFormValues.find(
      (draft) => draft.attachmentEntityId === attachment.id,
    );

    return draftValues?.inputValues || [];
  }, [attachment?.id, channelDraft.fillInTheBlankFormValues]);

  const [inputValues, setInputValues] = useState<string[][]>([...channelDraftInputValues]);

  const isFormValid = useCallback(() => {
    if (!inputValues.length) return false;

    const hasInvalidValues = !!inputValues.map((values) => values.some((value) => value === '')).includes(true);
    setShowErrorMessage(hasInvalidValues);

    return !hasInvalidValues;
  }, [inputValues]);

  // set initial values from draft state
  useEffect(() => {
    if (channelDraftInputValues.length) {
      setInputValues([...channelDraftInputValues]);
    }
  }, [channelDraftInputValues]);

  const handleFormSaved = useCallback(async (): Promise<void> => {
    if (!attachment?.id || !isFormValid()) {
      toast({
        title: 'Error',
        description: 'We were unable to save your changes.',
        status: 'error',
      });
      return;
    }

    setChannelDraft({
      fillInTheBlankFormValues: [
        ...channelDraft.fillInTheBlankFormValues.filter((values) => values.attachmentEntityId !== attachment.id),
        {
          attachmentEntityId: attachment.id,
          inputValues,
        },
      ],
    });
    onClose();
  }, [
    attachment?.id,
    channelDraft.fillInTheBlankFormValues,
    inputValues,
    toast,
    isFormValid,
    onClose,
    setChannelDraft,
  ]);

  const formTitle = useMemo(
    () =>
      attachment?.formTemplateContent?.formName
        ? `${attachment.formTemplateContent.formName} form`
        : 'form you selected',
    [attachment],
  );

  const fillInTheBlanks = useMemo((): IFillInTheBlank[] => {
    const fillInTheBlankArray: IFillInTheBlank[] = [];

    if (attachment?.formTemplateContent?.introMessage.includes(WildcardValues.FillInTheBlank)) {
      fillInTheBlankArray.push({ title: 'Introduction Message', body: attachment.formTemplateContent.introMessage });
    }

    (attachment?.formTemplateContent?.questions as IQuestion[])?.forEach((question, index) => {
      if (question.hasFillInTheBlanks) {
        fillInTheBlankArray.push({ title: `Question #${index + 1}`, body: question.prompt });
      }
    });

    return fillInTheBlankArray;
  }, [attachment]);

  const handleInputChange = (newValue: string, questionIndex: number, inputIndex: number): void => {
    setInputValues((currentValues) => {
      const newValues = [...currentValues];
      newValues[questionIndex][inputIndex] = newValue;
      return newValues;
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered finalFocusRef={undefined} scrollBehavior="inside">
      <ModalCloseButton />
      <ModalHeader>Please Fill in Open Fields in This Form</ModalHeader>
      <ModalBody>
        <VStack alignItems="flex-start">
          <Text>
            {`The "${formTitle}" has open fields for customized information. You can't send this message until you've filled out these fields.`}
          </Text>
          {showErrorMessage && (
            <Alert
              status="error"
              title="Unable to Save Your Changes"
              description="One or more questions below is missing a required fill in the blank value."
              hideCloseButton
            />
          )}
          {fillInTheBlanks.map((question, questionIndex) => (
            <VStack key={`fillInTheBlank_${questionIndex}`} w="100%" alignItems="stretch" pt={3}>
              <Text fontWeight="bold">{question.title}</Text>
              <Box bg="background.info" fontSize="sm" borderRadius="md" px={5} py={3} whiteSpace="break-spaces">
                <PromptPreview
                  prompt={question.body}
                  questionIndex={questionIndex}
                  inputValues={inputValues}
                  setInputValues={setInputValues}
                />
              </Box>
              {inputValues[questionIndex]?.map((value, inputIndex) => {
                const blankIndex =
                  inputValues[questionIndex].slice(0, inputIndex).filter((value) => typeof value === 'string').length +
                  1;
                const defaultValue = inputValues[questionIndex][inputIndex] || '';
                return (
                  value !== null && (
                    <Flex key={`input_${questionIndex}-${inputIndex}`}>
                      <Text size="sm" minW={5} alignSelf="center">{`${blankIndex}.`}</Text>
                      <TextInput
                        type="text"
                        placeholder="Enter custom information here..."
                        {...(showErrorMessage && { onBlur: isFormValid })}
                        defaultValue={defaultValue}
                        onChange={(event): void => handleInputChange(event.target?.value, questionIndex, inputIndex)}
                        size="sm"
                      />
                    </Flex>
                  )
                );
              })}
            </VStack>
          ))}
        </VStack>
      </ModalBody>
      <ModalFooterButtons onSubmit={handleFormSaved} submitText="Save" />
    </Modal>
  );
};

export default OpenFormFieldModal;
